import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { ClipLoader } from 'react-spinners';
import { XMarkIcon } from '@heroicons/react/24/solid';
import {
  ArrowTopRightOnSquareIcon,
  DocumentIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';

import { getBooking } from '../../redux/actions/bookings';
import {
  canReviewBooking,
  getEffectiveBookingStatus,
} from '../../utils/bookingActionability';
import { mapBookingError } from '../../utils/bookingErrorMapper';

/* ============================================================
   BookingReviewModal — redesign (promoter-side)
   Preserves the exact external contract of the original modal:
     props: { isOpen, onClose, payload, bookingId,
              notificationBookingStatus, initialBooking,
              onApprove, onReject }
   Effects unchanged: dispatch(getBooking(bookingId)) on open,
   action result handling, error mapping, status fetching.
   Only the JSX + styling changes.
   ============================================================ */

export default function BookingReviewModal({
  isOpen,
  onClose,
  payload,
  bookingId,
  notificationBookingStatus,
  initialBooking,
  onApprove,
  onReject,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [actionFailed, setActionFailed] = useState(false);
  const [liveBooking, setLiveBooking] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [freshLoaded, setFreshLoaded] = useState(false);
  const [fetchFailed, setFetchFailed] = useState(false);
  const [showFloorplan, setShowFloorplan] = useState(false);

  // Fetch fresh booking state whenever the modal opens
  useEffect(() => {
    if (!isOpen || !bookingId) return;

    let cancelled = false;

    const hint = {
      ...(payload || {}),
      ...(initialBooking || {}),
      status:
        initialBooking?.effectiveStatus ||
        initialBooking?.currentStatus ||
        initialBooking?.status ||
        notificationBookingStatus ||
        payload?.bookingStatus ||
        payload?.status,
      bookingStatus:
        initialBooking?.bookingStatus ||
        notificationBookingStatus ||
        payload?.bookingStatus,
      expireAt:
        initialBooking?.expireAt ||
        initialBooking?.expiresAt ||
        payload?.expireAt ||
        payload?.expiresAt,
    };

    setLiveBooking(hint);
    setActionError(null);
    setActionFailed(false);
    setFetching(true);
    setFreshLoaded(false);
    setFetchFailed(false);
    setShowFloorplan(false);

    dispatch(getBooking(bookingId))
      .then((booking) => {
        if (cancelled) return;
        setLiveBooking({ ...hint, ...(booking || {}) });
        setFreshLoaded(true);
      })
      .catch(() => {
        if (!cancelled) setFetchFailed(true);
      })
      .finally(() => {
        if (!cancelled) setFetching(false);
      });

    return () => { cancelled = true; };
  }, [isOpen, bookingId, dispatch, notificationBookingStatus, payload, initialBooking]);

  if (!isOpen) return null;

  const {
    brandName,
    eventName,
    placeName,
    placeSize,
    price,
    currency = 'EUR',
  } = payload || {};

  const liveStatus = getEffectiveBookingStatus(liveBooking);
  const canReview =
    freshLoaded && !fetching && !actionFailed && canReviewBooking(liveBooking);

  // Floorplan spot + submitted documents (T2B-382). Both ride on the fresh
  // getBooking response: placementId/documents are booking columns and the
  // Event include carries floorplanUrl/placements.
  const floorplanUrl = liveBooking?.Event?.floorplanUrl || null;
  const placements = Array.isArray(liveBooking?.Event?.placements)
    ? liveBooking.Event.placements
    : [];
  const requestedPlacement = liveBooking?.placementId
    ? placements.find((p) => p?.id === liveBooking.placementId) || null
    : null;
  const hasFloorplanSpot = Boolean(floorplanUrl && requestedPlacement);
  const documents = Array.isArray(liveBooking?.documents)
    ? liveBooking.documents.filter((d) => d?.url)
    : [];

  // Identity-row art, like the brand-side checkout modal (T2B-389): the fresh
  // getBooking response carries Brand/Event avatars and the Space size; the
  // notification payload is the fallback for stale/legacy notifications.
  const brandAvatar = liveBooking?.Brand?.avatar || payload?.brandLogo || null;
  const eventAvatar = liveBooking?.Event?.avatar || payload?.eventImage || null;
  const spaceSize = liveBooking?.Space?.size || placeSize || null;
  const spaceLabel = `${placeName || liveBooking?.Space?.name || '—'}${
    spaceSize ? ` · ${spaceSize} m²` : ''
  }`;

  // Pick which resolved-state banner to show (if any)
  const resolvedKind = (() => {
    if (fetchFailed) return 'error';
    if (fetching) return null;
    if (canReview) return null;
    if (actionFailed && !liveStatus) return 'error';
    switch (liveStatus) {
      case 'approved': return 'approved';
      case 'rejected': return 'rejected';
      case 'paid':     return 'paid';
      case 'expired':  return 'expired';
      default:         return 'error';
    }
  })();

  const headCopy = (() => {
    if (canReview) {
      return {
        eyebrow: t('alert_dialog.booking_review.eyebrow_new', 'New booking request'),
        title: t(
          'alert_dialog.booking_review.title_new_request_v2',
          'You have a request from {{brand}}.',
          { brand: brandName || t('common.brand', 'Brand') }
        ),
        sub: t(
          'alert_dialog.booking_review.sub_new_request',
          'They want a {{place}} space at {{event}}.\n\nApprove to lock it in or reject to release it back to the pool.',
          {
            place: `${placeName || t('common.space', 'Space')}${
              spaceSize ? ` · ${spaceSize} m²` : ''
            }`,
            event: eventName || t('common.event', 'Event'),
          }
        ),
      };
    }
    switch (resolvedKind) {
      case 'approved': return {
        eyebrow: t('alert_dialog.booking_review.eyebrow_details', 'Booking details'),
        title: t('alert_dialog.booking_review.title_approved', 'You approved this request.'),
        sub: t('alert_dialog.booking_review.sub_approved', '{{brand}} has been notified and now has until the event opens to pay. The space is held for them.', { brand: brandName }),
      };
      case 'rejected': return {
        eyebrow: t('alert_dialog.booking_review.eyebrow_details', 'Booking details'),
        title: t('alert_dialog.booking_review.title_rejected', 'You rejected this request.'),
        sub: t('alert_dialog.booking_review.sub_rejected', 'The space is back in the pool. {{brand}} has been notified.', { brand: brandName }),
      };
      case 'paid': return {
        eyebrow: t('alert_dialog.booking_review.eyebrow_details', 'Booking details'),
        title: t('alert_dialog.booking_review.title_paid', 'This booking is paid & confirmed.'),
        sub: t('alert_dialog.booking_review.sub_paid', '{{brand}} has paid and the space is theirs for the event.', { brand: brandName }),
      };
      case 'expired': return {
        eyebrow: t('alert_dialog.booking_review.eyebrow_details', 'Booking details'),
        title: t('alert_dialog.booking_review.title_expired', 'This request has expired.'),
        sub: t('alert_dialog.booking_review.sub_expired', 'The window to approve or reject has passed, so the space went back to the pool automatically.'),
      };
      case 'error':
      default: return {
        eyebrow: t('alert_dialog.booking_review.eyebrow_request', 'Booking request'),
        title: t('alert_dialog.booking_review.title_error', 'Something went sideways.'),
        sub: t(
          'alert_dialog.booking_review.unable_to_verify_v2',
          "We couldn't verify the current state of this booking. Refresh and try again."
        ),
      };
    }
  })();

  const run = async (fn) => {
    if (busy) return;
    setBusy(true);
    setActionError(null);
    try {
      const result = await fn(bookingId);
      if (result?.ok === false) {
        setActionFailed(true);
        try {
          const fresh = await dispatch(getBooking(bookingId));
          setLiveBooking((prev) => ({ ...(prev || {}), ...(fresh || {}) }));
          setFreshLoaded(true);
        } catch { /* ignore */ }
        const raw =
          result?.error?.response?.data?.message ||
          result?.error?.response?.data?.error ||
          result?.error?.message;
        const mapped = raw ? mapBookingError(raw, t) : null;
        setActionError(
          mapped || t('booking_errors.action_failed', 'Something went wrong. The booking may have changed status.')
        );
      } else {
        onClose();
      }
    } catch (err) {
      setActionFailed(true);
      const raw =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message;
      const mapped = raw ? mapBookingError(raw, t) : null;
      try {
        const fresh = await dispatch(getBooking(bookingId));
        setLiveBooking((prev) => ({ ...(prev || {}), ...(fresh || {}) }));
        setFreshLoaded(true);
      } catch { /* ignore */ }
      setActionError(
        mapped || t('booking_errors.action_failed', 'Something went wrong. The booking may have changed status.')
      );
    } finally {
      setBusy(false);
    }
  };

  // ----- render --------------------------------------------------

  const priceLabel = `${currency === 'EUR' ? '€' : ''}${Number(price || 0).toLocaleString()}`;

  return (
    <div
      id="modal-overlay"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-[rgba(20,18,12,.42)] backdrop-blur-sm px-4"
      onClick={(e) => e.target.id === 'modal-overlay' && onClose()}
    >
      <div
        className="brm relative bg-[#FAF9F6] rounded-[22px] shadow-[0_30px_80px_-20px_rgba(20,18,12,.4),0_0_0_1px_rgba(0,0,0,.04)] w-full max-w-[720px] max-h-[90vh] overflow-hidden flex flex-col"
        style={{ fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif", color: '#1A1A1F' }}
      >
        {/* Close */}
        <button
          className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/70 border border-[#ECE9E0] grid place-items-center hover:bg-white z-10"
          onClick={onClose}
          aria-label={t('common.close', 'Close')}
        >
          <XMarkIcon className="w-4 h-4 text-[#1A1A1F]" />
        </button>

        {/* HEAD */}
        <div
          className="px-9 pt-8 pb-6 border-b border-[#ECE9E0]"
          style={{
            background:
              'radial-gradient(ellipse 80% 90% at 0% 0%, rgba(255,189,89,.18), transparent 60%), #FAF9F6',
          }}
        >
          <div
            className="text-[11px] font-bold uppercase tracking-[0.14em] mb-3"
            style={{
              color: canReview ? '#7f6000' : '#8A8579',
              fontFamily: "'Manrope', 'Inter', sans-serif",
            }}
          >
            {headCopy.eyebrow}
          </div>
          <h2
            className="m-0 text-[36px] leading-[1.05] tracking-[-0.01em] pr-14"
            style={{ fontFamily: "'Instrument Serif', Georgia, serif", fontWeight: 400 }}
          >
            {headCopy.title}
          </h2>
          <p className="mt-3 text-[14.5px] leading-[1.45] text-[#6B675E] max-w-[56ch] pr-14 whitespace-pre-line">
            {headCopy.sub}
          </p>
        </div>

        {/* BODY */}
        <div className="px-9 pt-6 pb-2 overflow-y-auto">
          {/* Identity stack */}
          <div className="flex flex-col border border-[#ECE9E0] rounded-2xl bg-white overflow-hidden">
            <StackRow
              k={t('common.brand', 'Brand')}
              v={brandName || '—'}
              vs={payload?.brandHandle}
              iconChildren={
                brandAvatar ? (
                  <img src={brandAvatar} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: 16 }}>
                    {(brandName || '?').slice(0, 1)}
                  </span>
                )
              }
              iconBg="#FFF3DB"
            />
            <StackRow
              k={t('common.event', 'Event')}
              v={eventName || '—'}
              vs={payload?.eventWhen}
              iconChildren={
                eventAvatar ? (
                  <img src={eventAvatar} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-[#8A8579] text-xs">EV</span>
                )
              }
            />
            <StackRow
              k={t('common.space', 'Space')}
              v={spaceLabel}
              vs={payload?.placeMeta}
              iconChildren={
                <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: spaceSize ? 13 : 16 }}>
                  {spaceSize
                    ? `${spaceSize}m²`
                    : payload?.placeCode || (placeName || '?').slice(0, 2).toUpperCase()}
                </span>
              }
              right={
                price ? (
                  <div className="text-right" style={{ fontFamily: "'Manrope', sans-serif" }}>
                    <div className="font-bold text-[18px] tracking-[-0.005em] text-[#1A1A1F]">
                      {priceLabel}
                    </div>
                  </div>
                ) : null
              }
            />
          </div>

          {/* Requested floorplan spot (T2B-382) */}
          {hasFloorplanSpot && (
            <div className="mt-3.5 border border-[#ECE9E0] rounded-2xl bg-white overflow-hidden">
              <div className="flex items-center justify-between gap-3 px-5 py-4">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-xl bg-[#FFF3DB] grid place-items-center shrink-0">
                    <MapPinIcon className="w-[18px] h-[18px] text-[#7f6000]" />
                  </div>
                  <div className="min-w-0">
                    <div
                      className="text-[11px] uppercase tracking-[0.12em] font-bold text-[#8A8579]"
                      style={{ fontFamily: "'Manrope', sans-serif" }}
                    >
                      {t('alert_dialog.booking_review.spot_label', 'Requested spot')}
                    </div>
                    <div className="text-[13px] text-[#6B675E] mt-0.5">
                      {t(
                        'alert_dialog.booking_review.spot_hint',
                        'This request targets a specific spot on the floor plan.'
                      )}
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setShowFloorplan((v) => !v)}
                  className="shrink-0 px-4 py-2 rounded-full text-[13px] font-semibold border border-[#D9D5C7] bg-transparent text-[#1A1A1F] hover:bg-[#F4F2EC]"
                >
                  {showFloorplan
                    ? t('alert_dialog.booking_review.hide_floorplan', 'Hide floor plan')
                    : t('alert_dialog.booking_review.view_floorplan', 'View on floor plan')}
                </button>
              </div>
              {showFloorplan && (
                <div className="border-t border-[#ECE9E0] bg-[#F4F2EC] p-3">
                  <div className="relative rounded-xl overflow-hidden bg-white">
                    <img
                      src={floorplanUrl}
                      alt={t('dashboard.floorplan_tab', 'Floorplan')}
                      className="block w-full h-auto"
                    />
                    {/* Context: every other spot as a muted dot */}
                    {placements
                      .filter((p) => p?.id && p.id !== requestedPlacement.id)
                      .map((p) => (
                        <span
                          key={p.id}
                          className="absolute w-3.5 h-3.5 rounded-full bg-[#d1d5db] border-2 border-[#6B675E]"
                          style={{
                            left: `${(p.xPercent ?? 0) * 100}%`,
                            top: `${(p.yPercent ?? 0) * 100}%`,
                            transform: 'translate(-50%, -50%)',
                          }}
                        />
                      ))}
                    {/* The requested spot, highlighted */}
                    <span
                      className="absolute"
                      style={{
                        left: `${(requestedPlacement.xPercent ?? 0) * 100}%`,
                        top: `${(requestedPlacement.yPercent ?? 0) * 100}%`,
                        transform: 'translate(-50%, -50%)',
                      }}
                    >
                      <span className="absolute inset-0 -m-2 rounded-full bg-[#FFBD59]/50 animate-ping" />
                      <span className="relative block w-6 h-6 rounded-full bg-[#FFBD59] border-[3px] border-[#1A1A1F]" />
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Submitted documents (T2B-382) */}
          {documents.length > 0 && (
            <div className="mt-3.5 border border-[#ECE9E0] rounded-2xl bg-white px-5 py-4">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-[#F4F2EC] grid place-items-center shrink-0">
                  <DocumentIcon className="w-[18px] h-[18px] text-[#6B675E]" />
                </div>
                <div
                  className="text-[11px] uppercase tracking-[0.12em] font-bold text-[#8A8579]"
                  style={{ fontFamily: "'Manrope', sans-serif" }}
                >
                  {t('alert_dialog.booking_review.docs_label', 'Submitted documents')}
                </div>
              </div>
              <ul className="mt-3 space-y-1.5 pl-12">
                {documents.map((doc, idx) => (
                  <li key={idx}>
                    <a
                      href={doc.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-[13.5px] font-semibold text-[#7f6000] hover:underline"
                    >
                      <ArrowTopRightOnSquareIcon className="w-4 h-4 shrink-0" />
                      <span className="truncate">
                        {doc.name || t('booking.document', 'Document')}
                      </span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Loading */}
          {fetching && (
            <div className="mt-3.5 flex items-center gap-3 px-5 py-4 bg-[#F4F2EC] border border-[#ECE9E0] rounded-xl text-[#6B675E] text-[13.5px]"
                 style={{ fontFamily: "'Manrope', sans-serif" }}>
              <ClipLoader color="#1A1A1F" size={18} />
              <span>{t('alert_dialog.booking_review.verifying', 'Verifying current booking state…')}</span>
            </div>
          )}

          {/* Resolved status banners */}
          {!fetching && resolvedKind && (
            <StatusBanner kind={resolvedKind} t={t} brand={brandName} place={placeName} />
          )}

          {/* Action error (inline) */}
          {actionError && (
            <p className="mt-3.5 text-[13px] text-[#A12E2E] bg-[#F4DADA] rounded-xl px-4 py-3 font-medium">
              {actionError}
            </p>
          )}
        </div>

        {/* FOOTER */}
        <div
          className={`mt-2 px-7 py-5 border-t border-[#ECE9E0] bg-[#F4F2EC] flex items-center gap-3 ${canReview ? '' : 'justify-end'}`}
        >
          {canReview ? (
            <>
              <div className="flex-1 text-[12.5px] text-[#6B675E] italic leading-[1.4]"
                   style={{ fontFamily: "'Instrument Serif', Georgia, serif" }}>
                {t('alert_dialog.booking_review.approve_hint', 'Approving holds the space for {{brand}} until they pay.', { brand: brandName })}
              </div>
              <div className="flex gap-2.5 shrink-0">
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => run(onReject)}
                  className="px-5 py-3 rounded-full text-[14px] font-semibold border border-[#D9D5C7] bg-transparent text-[#A12E2E] hover:bg-[#F4DADA] hover:border-[#A12E2E] disabled:opacity-50"
                >
                  {t('dashboard.reject', 'Reject')}
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => run(onApprove)}
                  className="px-5 py-3 rounded-full text-[14px] font-semibold text-white bg-[#2C7A4B] border border-[#2C7A4B] hover:bg-[#1f5c38] hover:border-[#1f5c38] disabled:opacity-50"
                >
                  {t('dashboard.approve', 'Approve')}
                </button>
              </div>
            </>
          ) : (
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-3 rounded-full text-[14px] font-semibold text-white bg-[#1A1A1F] hover:bg-black"
            >
              {t('common.close', 'Close')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   Small presentational subcomponents
   ============================================================ */

function StackRow({ k, v, vs, iconChildren, iconBg = '#F4F2EC', right }) {
  return (
    <div className="grid grid-cols-[56px_1fr_auto] items-center gap-4 px-5 py-4 border-b border-[#ECE9E0] last:border-b-0">
      <div
        className="w-11 h-11 rounded-xl grid place-items-center overflow-hidden"
        style={{ background: iconBg }}
      >
        {iconChildren}
      </div>
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-[0.12em] font-bold text-[#8A8579]"
             style={{ fontFamily: "'Manrope', sans-serif" }}>
          {k}
        </div>
        <div className="text-[16px] font-semibold text-[#1A1A1F] leading-[1.3] mt-0.5">{v}</div>
        {vs && <div className="text-[12.5px] text-[#6B675E] mt-0.5">{vs}</div>}
      </div>
      <div>{right}</div>
    </div>
  );
}

function StatusBanner({ kind, t, brand, place }) {
  const palettes = {
    approved: { bg: '#DBEFE1', fg: '#2C7A4B', label: t('alert_dialog.booking_review.banner_approved_l', 'Approved') },
    rejected: { bg: '#F4DADA', fg: '#A12E2E', label: t('alert_dialog.booking_review.banner_rejected_l', 'Rejected') },
    paid:     { bg: '#DBEFE1', fg: '#2C7A4B', label: t('alert_dialog.booking_review.banner_paid_l', 'Paid & confirmed') },
    expired:  { bg: '#F4DADA', fg: '#A12E2E', label: t('alert_dialog.booking_review.banner_expired_l', 'Expired') },
    error:    { bg: '#F4DADA', fg: '#A12E2E', label: t('alert_dialog.booking_review.banner_error_l', "Couldn't verify") },
  };
  const p = palettes[kind] || palettes.error;

  const body = (() => {
    switch (kind) {
      case 'approved': return {
        v: t('alert_dialog.booking_review.banner_approved_v', 'Waiting on payment from {{brand}}.', { brand }),
        vs: t('alert_dialog.booking_review.banner_approved_vs', "If they don't pay before the event opens, the space is released automatically."),
      };
      case 'rejected': return {
        v: t('alert_dialog.booking_review.banner_rejected_v', "{{brand}} won't be at this event.", { brand }),
        vs: t('alert_dialog.booking_review.banner_rejected_vs', 'The space is open again.'),
      };
      case 'paid': return {
        v: t('alert_dialog.booking_review.banner_paid_v', '{{brand}} is locked in for {{place}}.', { brand, place }),
        vs: t('alert_dialog.booking_review.banner_paid_vs', 'Your payout lands 7 days after the event closes.'),
      };
      case 'expired': return {
        v: t('alert_dialog.booking_review.banner_expired_v', 'This request timed out before it could be reviewed.'),
        vs: t('alert_dialog.booking_review.banner_expired_vs', 'If {{brand}} still wants in, they need to send a new request.', { brand }),
      };
      case 'error':
      default: return {
        v: t('alert_dialog.booking_review.banner_error_v', 'The booking may have changed status on another device.'),
        vs: t('alert_dialog.booking_review.banner_error_vs', 'Refresh and try again.'),
      };
    }
  })();

  // Icon per kind
  const Icon = () => {
    if (kind === 'approved' || kind === 'paid') {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      );
    }
    if (kind === 'rejected') {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      );
    }
    if (kind === 'expired') {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
        </svg>
      );
    }
    // error
    return (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
        <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
      </svg>
    );
  };

  return (
    <div className="mt-3.5 grid grid-cols-[auto_1fr] gap-3.5 px-4 py-4 bg-[#F4F2EC] border border-[#ECE9E0] rounded-xl">
      <div
        className="w-[38px] h-[38px] rounded-[10px] grid place-items-center shrink-0"
        style={{ background: p.bg, color: p.fg }}
      >
        <Icon />
      </div>
      <div>
        <div
          className="text-[11px] uppercase tracking-[0.12em] font-bold"
          style={{ color: p.fg, fontFamily: "'Manrope', sans-serif" }}
        >
          {p.label}
        </div>
        <div className="text-[14.5px] font-semibold text-[#1A1A1F] leading-[1.4] mt-0.5">{body.v}</div>
        <div className="text-[12.5px] text-[#6B675E] leading-[1.45] mt-1">{body.vs}</div>
      </div>
    </div>
  );
}
